import { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import useSessionPersistedState from '../../hooks/useSessionPersistedState';

const DEFAULT_DATE_RANGE = { document_date: { type: 'date', start: null, end: null } };
const DEFAULT_PAGINATION = { page: 0, size: 10 };

const useDocumentFilters = ({ defaultQuery, searchOnChange = false, persistKey } = {}) => {
  const [selectedFilter, setSelectedFilter] = useSessionPersistedState(
    persistKey ? `${persistKey}:selectedFilter` : undefined,
    'document_number',
  );
  const [searchTerm, setSearchTerm] = useSessionPersistedState(persistKey ? `${persistKey}:searchTerm` : undefined, '');
  const [selectedStatus, setSelectedStatus] = useSessionPersistedState(
    persistKey ? `${persistKey}:selectedStatus` : undefined,
    '',
  );
  const [dateRange, setDateRange] = useSessionPersistedState(
    persistKey ? `${persistKey}:dateRange` : undefined,
    DEFAULT_DATE_RANGE,
  );
  const [includeResubmitted, setIncludeResubmitted] = useSessionPersistedState(
    persistKey ? `${persistKey}:includeResubmitted` : undefined,
    false,
  );
  const [pagination, setPagination] = useSessionPersistedState(
    persistKey ? `${persistKey}:pagination` : undefined,
    DEFAULT_PAGINATION,
  );

  const skipFilterPageReset = useRef(true);
  const skipSearchPageReset = useRef(true);

  const buildQuery = () => {
    const query = { ...defaultQuery, ...pagination };

    if (searchTerm) {
      query[selectedFilter] = searchTerm;
    }
    if (selectedStatus) {
      query.status = selectedStatus;
    }
    if (dateRange?.document_date?.start || dateRange?.document_date?.end) {
      const { start, end } = dateRange.document_date;
      const cleanDateObject = { ...(start && { start }), ...(end && { end }) };
      if (Object.keys(cleanDateObject).length) {
        query.document_date = JSON.stringify(cleanDateObject);
      }
    }
    if (includeResubmitted) {
      query.include_resubmitted = true;
    }

    return query;
  };

  const [currentQuery, setCurrentQuery] = useState(() => buildQuery());

  useEffect(() => {
    if (skipFilterPageReset.current) {
      skipFilterPageReset.current = false;
      return;
    }
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [selectedFilter, selectedStatus, dateRange, includeResubmitted, setPagination]);

  useEffect(() => {
    setCurrentQuery(buildQuery());
  }, [selectedFilter, selectedStatus, dateRange, pagination, includeResubmitted]);

  useEffect(() => {
    if (!searchOnChange) return;

    const handler = debounce(() => {
      setCurrentQuery(buildQuery());
      if (skipSearchPageReset.current) {
        skipSearchPageReset.current = false;
        return;
      }
      setPagination((prev) => ({ ...prev, page: 0 }));
    }, 500);

    handler();
    return () => handler.cancel();
  }, [searchTerm]);

  const handleFilterChange = (event) => setSelectedFilter(event.target.value);

  const handleSearchChange = (event) => setSearchTerm(event.target.value);

  const handleStatusChange = (status) => setSelectedStatus(status || '');

  const handleDateRangeChange = (newDateRange) => setDateRange(newDateRange);

  const handleIncludeResubmittedChange = (checked) => setIncludeResubmitted(checked);

  const handlePaginationChange = (page, size) => setPagination({ page, size });

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      setCurrentQuery(buildQuery());
      setPagination((prev) => ({ ...prev, page: 0 }));
    }
  };

  return {
    selectedFilter,
    searchTerm,
    currentQuery,
    selectedStatus,
    dateRange,
    includeResubmitted,
    handleFilterChange,
    handleSearchChange,
    handleSearchKeyPress,
    handleStatusChange,
    handleDateRangeChange,
    handleIncludeResubmittedChange,
    handlePaginationChange,
  };
};

export default useDocumentFilters;
